import { useState, useMemo } from "react";
import { useBudget } from "../context/BudgetContext";

function computeHealth({ runwaySettings, transactions, categories }) {
  const now = new Date();
  const thisKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const lastKey = (() => { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; })();
  const thisTxs = transactions.filter(tx => tx.date?.startsWith(thisKey));
  const lastTxs = transactions.filter(tx => tx.date?.startsWith(lastKey));
  const thisBurn = thisTxs.reduce((s, tx) => s + Number(tx.amount), 0);
  const lastBurn = lastTxs.reduce((s, tx) => s + Number(tx.amount), 0);
  const mrr  = Number(runwaySettings?.mrr) || 0;
  const cash = Number(runwaySettings?.cashBalance) || 0;
  const burn = Number(runwaySettings?.monthlyBurnOverride) || thisBurn;
  const netBurn = Math.max(burn - mrr, 0);
  const runway = netBurn > 0 && cash > 0 ? Math.floor(cash / netBurn) : null;

  let score = 0, max = 0;
  const issues = [];

  if (runway !== null) {
    max += 40;
    score += runway >= 18 ? 40 : runway >= 12 ? 32 : runway >= 9 ? 24 : runway >= 6 ? 16 : runway >= 3 ? 8 : 0;
    if (runway < 6)  issues.push({ text: `${runway}mo runway — dangerously low`, color: "var(--red)" });
    else if (runway < 12) issues.push({ text: `${runway}mo runway — aim for 12+`, color: "var(--amber)" });
  }

  const limCats = categories.filter(c => c.limit > 0);
  if (limCats.length > 0) {
    max += 30;
    const over = limCats.filter(c => thisTxs.filter(tx => tx.categoryId === c.id).reduce((s, tx) => s + Number(tx.amount), 0) > c.limit);
    score += Math.round((1 - over.length / limCats.length) * 30);
    if (over.length > 0) issues.push({ text: `${over.length} categor${over.length > 1 ? "ies" : "y"} over limit`, color: "var(--amber)" });
  }

  if (lastBurn > 0 && thisBurn > 0) {
    max += 20;
    const chg = (thisBurn - lastBurn) / lastBurn;
    score += chg <= 0 ? 20 : chg <= 0.1 ? 14 : chg <= 0.25 ? 8 : 0;
    if (chg > 0.1) issues.push({ text: `burn ↑${(chg * 100).toFixed(0)}% vs last month`, color: chg > 0.25 ? "var(--red)" : "var(--amber)" });
  }

  if (burn > 0) {
    max += 10;
    const cov = mrr / burn;
    score += cov >= 0.5 ? 10 : cov >= 0.25 ? 6 : cov >= 0.1 ? 3 : 0;
    if (mrr === 0) issues.push({ text: "add MRR in runway settings", color: "#555" });
    else if (cov < 0.25) issues.push({ text: `MRR covers ${(cov * 100).toFixed(0)}% of burn`, color: "var(--amber)" });
  }

  if (max === 0) return null;
  return { score: Math.round((score / max) * 100), issues };
}

function HealthScore({ data }) {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const { score, issues } = data;
  const color = score >= 70 ? "var(--green)" : score >= 40 ? "var(--amber)" : "var(--red)";
  const hover = score >= 70 ? "hover-green" : score >= 40 ? "hover-amber" : "hover-red";
  const label = score >= 70 ? "healthy" : score >= 40 ? "watch closely" : "critical";
  return (
    <div className={`card ${hover}`} style={{ marginBottom: 10, cursor: "pointer" }} onClick={() => setOpen(o => !o)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div className="label">FINANCIAL_HEALTH</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
            <span className="mono" style={{ fontSize: 32, fontWeight: 500, color, lineHeight: 1 }}>{score}</span>
            <span className="mono" style={{ fontSize: 10, color: "#555" }}>/100</span>
          </div>
        </div>
        <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          <span className="badge" style={{ background: "transparent", border: `1px solid ${color}44`, color, fontSize: 10 }}>{label}</span>
          <span className="mono" style={{ fontSize: 9, color: "#555" }}>{open ? "▲ collapse" : "▼ details"}</span>
        </div>
      </div>
      {open && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #111", display: "flex", flexDirection: "column", gap: 7 }}>
          {issues.length === 0
            ? <span className="mono" style={{ fontSize: 10, color: "#555" }}>all signals look good</span>
            : issues.map((issue, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: issue.color, flexShrink: 0 }} />
                <span className="mono" style={{ fontSize: 10, color: "#777" }}>{issue.text}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

function exportPDF({ monthlyBudget, totalSpent, remaining, categories, getCategorySpending, transactions }) {
  const now = new Date();
  const month = now.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const thisMonth = transactions.filter(tx => { const d = new Date(tx.date); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); });
  const thisMonthSpend = thisMonth.reduce((s, tx) => s + Number(tx.amount), 0);
  const lastMonth = transactions.filter(tx => { const d = new Date(tx.date); const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1); return d.getMonth() === lm.getMonth() && d.getFullYear() === lm.getFullYear(); });
  const lastMonthSpend = lastMonth.reduce((s, tx) => s + Number(tx.amount), 0);
  const mom = lastMonthSpend > 0 ? (((thisMonthSpend - lastMonthSpend) / lastMonthSpend) * 100).toFixed(1) : null;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Greenline — ${month}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Helvetica Neue',sans-serif;color:#111;padding:48px}
.header{display:flex;justify-content:space-between;margin-bottom:40px;padding-bottom:24px;border-bottom:2px solid #000}
.logo{font-size:20px;font-weight:700;color:#16a34a}.meta{text-align:right;color:#888;font-size:12px;line-height:1.8}
.kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:36px}
.kpi{border:1px solid #e5e7eb;border-radius:8px;padding:16px}.kpi-l{font-size:10px;color:#999;text-transform:uppercase;letter-spacing:.06em;margin-bottom:4px}
.kpi-v{font-size:26px;font-weight:600}.kpi-s{font-size:11px;color:#999;margin-top:3px}
.green{color:#16a34a}.amber{color:#d97706}.red{color:#dc2626}
table{width:100%;border-collapse:collapse;margin-bottom:36px}
th{font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:#999;padding:10px 12px;text-align:left;border-bottom:1px solid #e5e7eb;background:#f9fafb}
td{font-size:13px;padding:10px 12px;border-bottom:1px solid #f3f4f6}
.bar-bg{background:#f3f4f6;border-radius:99px;height:5px;overflow:hidden}.bar-fill{height:100%;border-radius:99px}
.footer{margin-top:48px;padding-top:20px;border-top:1px solid #e5e7eb;font-size:11px;color:#999;display:flex;justify-content:space-between}
</style></head><body>
<div class="header"><div><div class="logo">Greenline</div><div style="font-size:12px;color:#999;margin-top:3px">Financial Report</div></div>
<div class="meta"><div><strong>${month}</strong></div><div>Generated ${now.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div><div style="color:#16a34a;font-weight:600">Confidential</div></div></div>
<div class="kpis">
<div class="kpi"><div class="kpi-l">Monthly budget</div><div class="kpi-v">$${monthlyBudget.toLocaleString()}</div></div>
<div class="kpi"><div class="kpi-l">Total spent</div><div class="kpi-v ${totalSpent > monthlyBudget ? "red" : "amber"}">$${totalSpent.toLocaleString()}</div>${mom !== null ? `<div class="kpi-s">${Number(mom) > 0 ? "↑" : "↓"} ${Math.abs(mom)}% vs last month</div>` : ""}</div>
<div class="kpi"><div class="kpi-l">Remaining</div><div class="kpi-v ${remaining >= 0 ? "green" : "red"}">$${remaining.toLocaleString()}</div></div>
</div>
<table><thead><tr><th>Category</th><th>Limit</th><th>Spent</th><th>Remaining</th><th>Usage</th></tr></thead><tbody>
${categories.map(cat => { const spent = getCategorySpending(cat.id); const rem = cat.limit > 0 ? cat.limit - spent : null; const pct = cat.limit > 0 ? Math.min((spent/cat.limit)*100,100) : 0; const bc = pct>=100?"#dc2626":pct>=80?"#d97706":"#16a34a"; return `<tr><td><strong>${cat.name}</strong></td><td>${cat.limit > 0 ? "$"+cat.limit.toLocaleString() : "—"}</td><td>$${spent.toLocaleString()}</td><td class="${rem !== null ? (rem < 0 ? "red" : "green") : ""}">${rem !== null ? "$"+rem.toLocaleString() : "—"}</td><td>${cat.limit > 0 ? `<div class="bar-bg"><div class="bar-fill" style="width:${pct}%;background:${bc}"></div></div>` : "—"}</td></tr>`; }).join("")}
</tbody></table>
<table><thead><tr><th>Date</th><th>Description</th><th>Amount</th></tr></thead><tbody>
${[...transactions].reverse().slice(0,10).map(tx=>`<tr><td style="color:#999">${tx.date}</td><td>${tx.description}</td><td style="font-weight:600;color:#dc2626">-$${Number(tx.amount).toLocaleString()}</td></tr>`).join("")}
${transactions.length===0?'<tr><td colspan="3" style="color:#999;text-align:center">No transactions</td></tr>':""}
</tbody></table>
<div class="footer"><span>Greenline — Confidential</span><span>${month}</span></div>
</body></html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 500);
}

function SpendingChart({ transactions, monthlyBudget }) {
  const now = new Date();
  const months = useMemo(() => Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const total = transactions
      .filter(tx => tx.date?.startsWith(key))
      .reduce((s, tx) => s + Number(tx.amount), 0);
    return { key, label: d.toLocaleDateString("en-US", { month: "short" }), total, isCurrent: i === 5 };
  }), [transactions]);

  const maxVal = Math.max(...months.map(m => m.total), monthlyBudget || 0, 1);

  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span className="label" style={{ marginBottom: 0 }}>SPENDING_HISTORY</span>
        {monthlyBudget > 0 && (
          <span className="mono" style={{ fontSize: 8, color: "var(--green-mid)" }}>
            — ${monthlyBudget.toLocaleString()} budget
          </span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 80 }}>
        {months.map(({ key, label, total, isCurrent }) => (
          <div key={key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <span className="mono" style={{ fontSize: 8, color: isCurrent && total > 0 ? "var(--green)" : "transparent", lineHeight: 1 }}>
              ${total >= 1000 ? (total / 1000).toFixed(1) + "k" : total}
            </span>
            <div style={{ width: "100%", background: "#111", borderRadius: 3, overflow: "hidden", height: 52, display: "flex", alignItems: "flex-end" }}>
              <div style={{
                width: "100%",
                height: `${Math.max((total / maxVal) * 100, total > 0 ? 3 : 0)}%`,
                background: isCurrent ? "var(--green)" : "#1a1a1a",
                borderRadius: "2px 2px 0 0",
                transition: "height 0.4s ease",
              }} />
            </div>
            <span className="mono" style={{ fontSize: 8, color: isCurrent ? "var(--green)" : "#2a2a2a", lineHeight: 1 }}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const toMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

export default function Dashboard() {
  const { monthlyBudget, categories, transactions, runwaySettings, budgetLoading } = useBudget();
  const [exporting, setExporting]       = useState(false);
  const [alertsDismissed, setAlertsDismissed] = useState(false);

  const [viewMonth, setViewMonth] = useState(() => toMonthKey(new Date()));

  const nowKey     = toMonthKey(new Date());
  const isNow      = viewMonth === nowKey;
  const monthLabel = new Date(viewMonth + "-15").toLocaleDateString("en-US", { month: "long", year: "numeric" }).toLowerCase();

  const prevMonth = () => {
    const [y, m] = viewMonth.split("-").map(Number);
    const d = new Date(y, m - 2, 1);
    setViewMonth(toMonthKey(d));
  };
  const nextMonth = () => {
    if (isNow) return;
    const [y, m] = viewMonth.split("-").map(Number);
    const d = new Date(y, m, 1);
    if (toMonthKey(d) <= nowKey) setViewMonth(toMonthKey(d));
  };

  const monthTxs  = transactions.filter(tx => tx.date?.startsWith(viewMonth));
  const monthSpent = monthTxs.reduce((s, tx) => s + Number(tx.amount), 0);
  const monthRemaining = monthlyBudget - monthSpent;
  const spentPct  = monthlyBudget > 0 ? Math.min((monthSpent / monthlyBudget) * 100, 100) : 0;
  const barClass  = spentPct >= 100 ? "red" : spentPct >= 75 ? "amber" : "";

  const getMonthCategorySpending = (catId) =>
    monthTxs.filter(tx => tx.categoryId === catId).reduce((s, tx) => s + Number(tx.amount), 0);

  const alerts = useMemo(() =>
    categories.filter(cat => {
      if (cat.limit <= 0) return false;
      const pct = (getMonthCategorySpending(cat.id) / cat.limit) * 100;
      return pct >= 80;
    }),
  [categories, monthTxs]);

  const health = useMemo(() => computeHealth({ runwaySettings, transactions, categories }), [runwaySettings, transactions, categories]);

  if (budgetLoading) return <div className="page-loading" />;

  if (transactions.length === 0 && categories.every(c => c.limit === 0) && monthlyBudget === 0) {
    return (
      <div>
        <div className="page-header">
          <div>
            <div className="page-title">Overview</div>
            <div className="page-subtitle">$ {monthLabel}</div>
          </div>
        </div>
        <div className="card" style={{ padding: 40, textAlign: "center" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)", margin: "0 auto 16px" }} />
          <div className="mono" style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>your workspace is ready</div>
          <div className="mono" style={{ fontSize: 10, color: "#555", lineHeight: 1.8 }}>
            go to <span style={{ color: "#555" }}>settings</span> to set a monthly budget<br />
            then add transactions to start tracking
          </div>
        </div>
      </div>
    );
  }

  const handleExport = () => {
    setExporting(true);
    const getCategorySpending = (catId) =>
      transactions.filter(tx => tx.categoryId === catId).reduce((s, tx) => s + Number(tx.amount), 0);
    setTimeout(() => {
      exportPDF({ monthlyBudget, totalSpent: monthSpent, remaining: monthRemaining, categories, getCategorySpending, transactions: monthTxs });
      setExporting(false);
    }, 100);
  };

  return (
    <div>
      <div className="page-header" style={{ flexWrap: "wrap", gap: 10 }}>
        <div>
          <div className="page-title">Overview</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            <button onClick={prevMonth} className="btn" style={{ padding: "2px 8px", fontSize: 10 }}>←</button>
            <span className="mono" style={{ fontSize: 10, color: "#555", minWidth: 90, textAlign: "center" }}>$ {monthLabel}</span>
            <button onClick={nextMonth} className="btn" style={{ padding: "2px 8px", fontSize: 10, opacity: isNow ? 0.3 : 1 }} disabled={isNow}>→</button>
          </div>
        </div>
        <button className="btn" onClick={handleExport} disabled={exporting} style={{ alignSelf: "flex-start" }}>
          {exporting ? "generating..." : "↓ export pdf"}
        </button>
      </div>

      {/* Budget alerts */}
      {alerts.length > 0 && !alertsDismissed && (
        <div className="card hover-amber" style={{ marginBottom: 10, borderColor: "var(--amber)33" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div className="label" style={{ marginBottom: 8 }}>BUDGET_ALERTS</div>
            <button onClick={() => setAlertsDismissed(true)} className="btn" style={{ padding: "1px 6px", fontSize: 9 }}>✕</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {alerts.map(cat => {
              const spent = getMonthCategorySpending(cat.id);
              const pct   = Math.round((spent / cat.limit) * 100);
              const over  = pct >= 100;
              return (
                <div key={cat.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span className="mono" style={{ fontSize: 11, color: over ? "var(--red)" : "var(--amber)" }}>
                    {over ? "✕" : "!"} {cat.name}
                  </span>
                  <span className="mono" style={{ fontSize: 10, color: "#555" }}>
                    {pct}% — ${spent.toLocaleString()} / ${cat.limit.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="stat-grid stat-grid-3" style={{ marginBottom: 10 }}>
        {[
          { label: "MONTHLY_BUDGET", value: `$${monthlyBudget.toLocaleString()}`,   sub: "configured",                                              hover: "hover-green" },
          { label: "MONTH_SPENT",    value: `$${monthSpent.toLocaleString()}`,       sub: `${spentPct.toFixed(1)}%`,                                 hover: "hover-amber" },
          { label: "REMAINING",      value: `$${monthRemaining.toLocaleString()}`,   sub: monthRemaining < 0 ? "over budget" : "available",          hover: monthRemaining < 0 ? "hover-red" : "hover-blue" },
        ].map(({ label, value, sub, hover }) => (
          <div key={label} className={`card ${hover}`}>
            <div className="label">{label}</div>
            <div className="value">{value}</div>
            <div className="mono" style={{ fontSize: 10, color: "#666", marginTop: 3 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Progress */}
      <div className="card hover-green" style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span className="label" style={{ marginBottom: 0 }}>BUDGET_USAGE</span>
          <span className="mono" style={{ fontSize: 10, color: "#777" }}>{spentPct.toFixed(1)}%</span>
        </div>
        <div className="progress-track">
          <div className={`progress-fill ${barClass}`} style={{ width: `${spentPct}%` }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
          <span className="mono" style={{ fontSize: 9, color: "#2a2a2a" }}>$0</span>
          <span className="mono" style={{ fontSize: 9, color: "#2a2a2a" }}>${monthlyBudget.toLocaleString()}</span>
        </div>
      </div>

      {/* Financial health score */}
      <HealthScore data={health} />

      {/* Spending history chart */}
      <SpendingChart transactions={transactions} monthlyBudget={monthlyBudget} />

      {/* Category breakdown for selected month */}
      <div className="card">
        <div className="label" style={{ marginBottom: 12 }}>CATEGORY_BREAKDOWN</div>
        {categories.length === 0 ? (
          <p className="mono" style={{ fontSize: 11, color: "#666" }}>no categories — add some in categories page</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {categories.map(cat => {
              const spent   = getMonthCategorySpending(cat.id);
              const pct     = cat.limit > 0 ? Math.min((spent / cat.limit) * 100, 100) : 0;
              const fillClass = pct >= 100 ? "red" : pct >= 75 ? "amber" : "";
              return (
                <div key={cat.id}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span className="mono" style={{ fontSize: 11, color: "#666" }}>{cat.name}</span>
                    <span className="mono" style={{ fontSize: 10, color: "#666" }}>
                      ${spent.toLocaleString()}{cat.limit > 0 ? ` / $${cat.limit.toLocaleString()}` : ""}
                    </span>
                  </div>
                  <div className="progress-track">
                    <div className={`progress-fill ${fillClass}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
